import { AlertTriangle, RefreshCw, Zap, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useAutomationRuleStats, useAutomationExecutionStats } from '../../hooks/useAnalytics';

interface AutomationHealthWidgetProps {
  title?: string;
  loading?: boolean;
  className?: string;
}

export function AutomationHealthWidget({
  loading: externalLoading = false,
  className = '',
  title,
}: AutomationHealthWidgetProps) {
  const { data: ruleStats, isLoading: rulesLoading, error: rulesError } = useAutomationRuleStats();
  const { data: execStats, isLoading: execLoading, error: execError } = useAutomationExecutionStats();

  const loading = externalLoading || rulesLoading || execLoading;
  const error = rulesError || execError;

  if (error) {
    return (
      <div className={`border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`}>
        <div className="flex flex-col items-center justify-center text-center flex-1 p-4">
          <AlertTriangle className="h-8 w-8 text-red-400 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Failed to load automation data</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2 animate-pulse" />
        </div>
        <div className="flex-1 min-h-0 p-3 space-y-3">
          <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-6 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const totalRules = ruleStats?.total ?? 0;
  const activeRules = ruleStats?.active ?? 0;
  const totalExec = execStats?.total ?? 0;
  const completed = execStats?.completed ?? 0;
  const failed = execStats?.failed ?? 0;
  const pending = (execStats?.pending ?? 0) + (execStats?.running ?? 0);
  const successRate = totalExec > 0 ? Math.round((completed / totalExec) * 100) : 0;

  const hasNoData = totalRules === 0 && totalExec === 0;

  if (hasNoData) {
    return (
      <div className={`border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`}>
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Automation Health</h3>
        </div>
        <div className="flex flex-col items-center justify-center text-center flex-1 p-4">
          <Zap className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No Automations</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Create automation rules to see health metrics</p>
        </div>
      </div>
    );
  }

  const maxBar = Math.max(completed, failed, pending, 1);

  return (
    <div className={`border border-[var(--co-line)] bg-[var(--co-raised)] rounded-[10px] shadow-[var(--co-shadow-card)] h-full flex flex-col overflow-hidden ${className}`}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title || 'Automation Health'}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Rule execution statistics</p>
      </div>

      {/* Mini KPIs */}
      <div className="px-4 py-3 grid grid-cols-2 gap-3 shrink-0">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">Active Rules</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{activeRules}<span className="text-xs font-normal text-gray-400 dark:text-gray-500">/{totalRules}</span></p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2.5">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">Success Rate</p>
          <p className={`text-lg font-bold ${successRate >= 90 ? 'text-green-600' : successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
            {successRate}%
          </p>
        </div>
      </div>

      {/* Execution breakdown */}
      <div className="flex-1 min-h-0 px-4 pb-3 space-y-2">
        <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Executions ({totalExec})</p>
        {[
          { label: 'Completed', value: completed, color: 'bg-green-500', icon: CheckCircle, iconColor: 'text-green-500' },
          { label: 'Failed', value: failed, color: 'bg-red-500', icon: XCircle, iconColor: 'text-red-500' },
          { label: 'Pending', value: pending, color: 'bg-gray-400', icon: Clock, iconColor: 'text-gray-400 dark:text-gray-500' },
        ].map(({ label, value, color, icon: Icon, iconColor }) => (
          <div key={label} className="flex items-center gap-2">
            <Icon className={`w-3.5 h-3.5 ${iconColor} shrink-0`} />
            <span className="text-xs text-gray-600 dark:text-gray-400 w-16 shrink-0">{label}</span>
            <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${color} rounded-full transition-all`}
                style={{ width: `${(value / maxBar) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-900 dark:text-white w-8 text-right shrink-0">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
